/**
 * Safe parent origin resolution for iframe → parent postMessage calls.
 *
 * When SeatPilot pages (Builder, Checkout) are embedded in a 3rd-party site
 * via the SDK, the iframe needs to send postMessage to the parent without
 * leaking data to arbitrary listeners. Using '*' as the target origin is
 * unsafe because any window can intercept the message.
 *
 * Strategy:
 *   1) Read `?origin=` query param (preferred — set by the SDK)
 *   2) Fall back to `document.referrer`'s origin
 *   3) Last resort: '*' (only when neither is available, e.g. dev/local file://)
 */

// Use console.error so it survives our production build console-strip
// (we keep `error` level via `transform-remove-console` exclude list).
const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

export function getEmbedTargetOrigin() {
  try {
    const params = new URLSearchParams(window.location.search);
    const declared = params.get('origin') || params.get('parent_origin');
    if (declared) {
      const u = new URL(declared);
      return u.origin;
    }
  } catch (err) {
    // Malformed origin/parent_origin query param — fall through to referrer
    if (isDev) console.warn('[embedOrigin] invalid origin query param:', err?.message || err);
  }

  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch (err) {
    // Malformed referrer — fall through to '*'
    if (isDev) console.warn('[embedOrigin] invalid document.referrer:', err?.message || err);
  }

  // Last resort — broadcast. In production this is rare (only file:// or
  // privacy-restricted contexts where referrer is stripped). In dev, warn.
  if (isDev) console.warn('[embedOrigin] falling back to "*" — parent origin unavailable');
  return '*';
}
